import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@features/auth/contexts/AuthContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@shared/components/ui/card";
import { Button } from "@shared/components/ui/button";
import { Badge } from "@shared/components/ui/badge";
import HeadLayout from "@shared/layouts/HeadLayout";
import useTitle from "@shared/hooks/useTitle";
import { DatePickerWithRange } from "@shared/components/date-picker-with-range";
import { startOfDay, endOfDay, subDays } from "date-fns";
import {
  PackageIcon,
  Plus,
  RefreshCw,
  TrendingUp,
  CheckCircle,
  Eye,
  FileText,
  CreditCard,
  Target,
  TrendingDown,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useOverview } from "@features/dashboard/hooks/useStatstics";
import { STATUS_COLORS, STATUS_OPTIONS } from "@shared/utils/constants";
import { useIsMobile } from "@shared/hooks/use-mobile";
import PackageCard from "../components/PackageCard";
import StatCard from "../components/StatCard";
import BankingInfoBanner from "../components/BankingInfoBanner";

export default function ClientDashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [params, setParams] = useState({
    from: startOfDay(subDays(new Date(), 1000)), // Last 30 days instead of 5000
    to: endOfDay(new Date()),
  });

  useTitle(t("navigation.dashboard"));

  // Get overview statistics with enhanced hook
  const {
    data: stats,
    isLoading: isOverviewLoading,
    refetch,
  } = useOverview(params, {
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnWindowFocus: false,
  });

  console.log("Overview Statistics:", stats);

  const handleAddDate = (date) => {
    if (!date?.from || !date?.to) {
      return;
    }

    // Validate date range (max 1 year)
    // const maxDays = 1000;
    // const daysDiff = Math.ceil(
    //   (date.to.getTime() - date.from.getTime()) / (24 * 60 * 60 * 1000)
    // );

    // if (daysDiff > maxDays) {
    //   toast.error(
    //     t("dashboard.dateRangeError", {
    //       days: daysDiff,
    //       maxDays,
    //     }) ||
    //       `Date range too large (${daysDiff} days). Maximum allowed is ${maxDays} days.`
    //   );
    //   return;
    // }

    setParams((prev) => ({
      ...prev,
      from: startOfDay(date.from),
      to: endOfDay(date.to),
    }));
  };

  const handleRefresh = async () => {
    try {
      await refetch(true); // Use enhanced refetch with toast
    } catch (error) {
      console.error("Dashboard refresh error:", error);
      // Error handling is now done in the enhanced refetch function
    }
  };

  return (
    <div className="space-y-6">
      <HeadLayout title={t("navigation.dashboard")}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
          <div className="flex items-center gap-2">
            <DatePickerWithRange
              initialDate={params.date}
              onSelectDate={handleAddDate}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isOverviewLoading}
              className="flex items-center gap-2"
            >
              <RefreshCw
                className={`h-4 w-4 ${isOverviewLoading ? "animate-spin" : ""}`}
              />
              {!isMobile && t("common.refresh")}
            </Button>
          </div>
        </div>
      </HeadLayout>

      {/* Banking Info Banner - Shows if banking info is incomplete/unverified */}
      <BankingInfoBanner />

      {/* Welcome Section */}
      <Card className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 border-blue-200 dark:border-blue-800">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                {t("dashboard.welcome", {
                  name: user?.brandName || user?.name,
                })}
              </h2>
              <p className="text-blue-700 dark:text-blue-300 mt-1">
                {t("dashboard.clientWelcomeMessage")}
              </p>
            </div>
            <Button
              onClick={() => navigate("/packages/create")}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t("packages.createPackage")}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      {isOverviewLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-muted rounded w-1/2 mb-2"></div>
                  <div className="h-8 bg-muted rounded w-1/3 mb-2"></div>
                  <div className="h-3 bg-muted rounded w-1/4"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : stats ? (
        <div className="space-y-6">
          {/* Primary Metrics */}
          <div>
            <h3 className="text-lg font-semibold mb-4">
              {t("dashboard.packageMetrics") || "Package Metrics"}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <StatCard
                title={t("dashboard.totalPackages")}
                value={`${stats.packages.total}`}
                icon={PackageIcon}
                color="blue"
                trend={
                  stats.packages.total > 0
                    ? `${stats.packages.deliveryRate}% ${
                        t("dashboard.delivered") || "delivered"
                      }`
                    : undefined
                }
              />
              <StatCard
                title={t("dashboard.deliveredPackages")}
                value={stats.packages.delivered}
                icon={CheckCircle}
                color="green"
              />

              <StatCard
                title={t("dashboard.deliveryRate") || "Delivery Rate"}
                value={`${stats.packages.deliveryRate}%`}
                icon={Target}
                color={
                  stats.packages.deliveryRate >= 80
                    ? "green"
                    : stats.packages.deliveryRate >= 60
                    ? "orange"
                    : "red"
                }
              />

              <StatCard
                title={t("dashboard.totalCost") || "Total Cost"}
                value={`${stats.amounts.totalCost.toFixed(2)} MAD`}
                icon={TrendingDown}
                color="red"
                trend={
                  stats.amounts.totalRevenue > 0
                    ? `${(
                        (stats.amounts.totalCost / stats.amounts.totalRevenue) *
                        100
                      ).toFixed(1)}% ${
                        t("dashboard.ofRevenue") || "of revenue"
                      }`
                    : undefined
                }
              />
            </div>
          </div>

          {/* Financial Metrics */}
          <div>
            <h3 className="text-lg font-semibold mb-4">
              {t("dashboard.financialMetrics") || "Financial Metrics"}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <StatCard
                title={t("dashboard.totalInvoices") || "Total Invoices"}
                value={stats.invoices.totalInvoices}
                icon={FileText}
                color="blue"
                trend={
                  stats.invoices.paymentRate > 0
                    ? `${stats.invoices.paymentRate}% ${
                        t("dashboard.paid") || "paid"
                      }`
                    : `${0}%`
                }
              />
              <StatCard
                title={t("dashboard.paidInvoices") || "Paid Invoices"}
                value={stats.invoices.totalPaid}
                icon={CreditCard}
                color="green"
                trend={
                  stats.invoices.total > 0
                    ? `${(
                        (stats.invoices.paid / stats.invoices.total) *
                        100
                      ).toFixed(1)}% ${t("dashboard.ofTotal") || "of total"}`
                    : undefined
                }
              />
              <StatCard
                title={t("dashboard.totalRevenue")}
                value={`${stats.amounts.totalAmount.toFixed(2)} MAD`}
                icon={TrendingUp}
                color="purple"
                trend={
                  stats.profitMargin > 0
                    ? `${stats.profitMargin}% ${
                        t("dashboard.profitMargin") || "profit margin"
                      }`
                    : undefined
                }
              />
              <StatCard
                title={t("dashboard.totalProfit") || "Total Profit"}
                value={`${stats.amounts.totalProfit.toFixed(2)} MAD`}
                icon={Target}
                color="green"
                trend={
                  stats.amounts.totalAmount > 0
                    ? `${(
                        (stats.amounts.totalProfit /
                          stats.amounts.totalAmount) *
                        100
                      ).toFixed(1)}% ${t("dashboard.margin") || "margin"}`
                    : undefined
                }
              />
            </div>
          </div>
        </div>
      ) : null}

      {/* Recent Packages Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <PackageIcon className="h-5 w-5" />
                {t("dashboard.recentPackages")}
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/packages")}
              >
                {t("common.viewAll")}
              </Button>
            </CardHeader>
            <CardContent>
              {isOverviewLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Card key={i} className="border-l-2 border-l-transparent">
                      <CardContent className="p-3">
                        <div className="animate-pulse">
                          {/* Compact header skeleton */}
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 bg-muted rounded"></div>
                              <div className="w-16 h-4 bg-muted rounded"></div>
                            </div>
                            <div className="w-16 h-4 bg-muted rounded"></div>
                          </div>

                          {/* Customer info skeleton */}
                          <div className="flex justify-between items-center mb-2">
                            <div className="flex-1">
                              <div className="h-4 bg-muted rounded w-2/3 mb-1"></div>
                              <div className="h-3 bg-muted rounded w-1/2"></div>
                            </div>
                            <div className="w-12 h-3 bg-muted rounded"></div>
                          </div>

                          {/* Tracking skeleton */}
                          <div className="bg-muted/30 rounded px-2 py-1 border border-dashed">
                            <div className="h-3 bg-muted rounded w-1/2"></div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : stats.recentPackages.length > 0 ? (
                <div className="space-y-3">
                  {stats.recentPackages.map((pkg) => (
                    <PackageCard key={pkg._id} pkg={pkg} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <PackageIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>{t("packages.noPackages")}</p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => navigate("/packages/create")}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {t("packages.createFirst")}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t("dashboard.quickActions")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                className="w-full justify-start"
                variant="outline"
                onClick={() => navigate("/packages/create")}
              >
                <Plus className="h-4 w-4 mr-2" />
                {t("packages.createPackage")}
              </Button>
              <Button
                className="w-full justify-start"
                variant="outline"
                onClick={() => navigate("/packages")}
              >
                <PackageIcon className="h-4 w-4 mr-2" />
                {t("packages.viewPackages")}
              </Button>
              <Button
                className="w-full justify-start"
                variant="outline"
                onClick={() => navigate("/account")}
              >
                <Eye className="h-4 w-4 mr-2" />
                {t("user.account")}
              </Button>
            </CardContent>
          </Card>

          {/* Package Status Overview */}
          {stats?.packages && (
            <Card>
              <CardHeader>
                <CardTitle>{t("dashboard.packageStatusOverview")}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Mini Chart */}
                {/* <div className="mb-4">
                  <MiniPackageChart data={overviewData.packageStatus} />
                </div> */}

                {/* Status List */}
                {stats.raw.packageStatus.map((status) => (
                  <div
                    key={status._id}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <Badge
                        className={STATUS_COLORS[status._id]}
                        variant="outline"
                      >
                        {t(STATUS_OPTIONS[status._id])}
                      </Badge>
                    </div>
                    <span className="font-medium">{status.count}</span>
                  </div>
                ))}

                {/* Performance metrics */}
                {stats && (
                  <div className="mt-4 pt-3 border-t border-muted">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">
                          {t("dashboard.deliveryRate")}
                        </span>
                        <span
                          className={`font-medium ${
                            stats.packages.deliveryRate >= 90
                              ? "text-green-600"
                              : stats.packages.deliveryRate >= 70
                              ? "text-orange-600"
                              : "text-red-600"
                          }`}
                        >
                          {stats.packages.deliveryRate}%
                        </span>
                      </div>

                      {stats.amounts.profitMargin > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">
                            {t("dashboard.profitMargin")}
                          </span>
                          <span className="font-medium text-purple-600">
                            {stats.amounts.profitMargin}%
                          </span>
                        </div>
                      )}

                      {stats.amounts.paymentRate > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">
                            {t("dashboard.paymentRate") || "Payment Rate"}
                          </span>
                          <span
                            className={`font-medium ${
                              stats.amounts.paymentRate >= 90
                                ? "text-green-600"
                                : stats.amounts.paymentRate >= 70
                                ? "text-orange-600"
                                : "text-red-600"
                            }`}
                          >
                            {stats.amounts.paymentRate}%
                          </span>
                        </div>
                      )}

                      {stats.deliveredPackages > 0 && (
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">
                            {t("dashboard.avgOrderValue") || "Avg Order"}
                          </span>
                          <span className="font-medium text-blue-600">
                            {(
                              stats.amounts.total / stats.packages.delivered
                            ).toFixed(0)}{" "}
                            MAD
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Performance indicators */}
                    <div className="mt-3 pt-2 border-t border-muted/50">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">
                          {t("dashboard.performanceIndicator") || "Performance"}
                        </span>
                        <div className="flex items-center gap-1">
                          {stats.packages.deliveryRate >= 90 &&
                          stats.amounts.paymentRate >= 90 ? (
                            <>
                              <div className="w-2 h-2 rounded-full bg-green-500"></div>
                              <span className="text-green-600 font-medium">
                                {t("dashboard.excellent") || "Excellent"}
                              </span>
                            </>
                          ) : stats.packages.deliveryRate >= 70 &&
                            stats.amounts.paymentRate >= 70 ? (
                            <>
                              <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                              <span className="text-orange-600 font-medium">
                                {t("dashboard.good") || "Good"}
                              </span>
                            </>
                          ) : (
                            <>
                              <div className="w-2 h-2 rounded-full bg-red-500"></div>
                              <span className="text-red-600 font-medium">
                                {t("dashboard.needsImprovement") ||
                                  "Needs Improvement"}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
